import { connect } from 'react-redux';
import React, { useEffect, FunctionComponent } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { FormPackType } from '@upstart/harmony/lib/constants';
import { History } from 'history';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { FormPack } from '@upstart/harmony/lib/legos/formTypes';
import formPacksActions from '../../redux/formPacks/actions';
import OnlineFormPackList from '../../components/OnlineFormPackList';
import SidebarContainer from '../SidebarContainer';
import DealerNav from '../../components/DealerNav';
import ContentPage from '../ContentPage';
import { getOnlineFormPackMap } from '../OnlineFormPack/constants';
import './OnlineFormPacks.scss';

interface FormPacks {
  formPacks: FormPack[];
  error: string | null;
  fetching: boolean;
}

interface Props {
  match: {
    params: {
      formPackId: string;
      dealerId: string;
    };
  };
  actions: {
    formPacksRequest: Function;
  };
  formPacks: FormPacks;
  dealer: Dealer;
  history: History;
}

// Key defaults by dealer id
const getDefaultOnlineFormPacks = (dealer: Dealer) => {
  const onlineFormPackMap = getOnlineFormPackMap(dealer);
  return Object.entries(onlineFormPackMap).map(([key, value]) => ({
    id: `${dealer.id}-${key}`,
    // @ts-ignore
    name: value.name,
    dealer_id: dealer.id,
    type: FormPackType.ONLINE,
    FormTemplates: [],
  }));
};

// These are immutable for each dealer
// 1. Check if form pack exists in the existing list
// 2. If it does, use it.
// 3. If not, use the default
const getOnlineFormPacks = (
  dealer: Dealer,
  existingFormPackList: FormPack[],
): FormPack[] => {
  const onlineFormPackMap = getOnlineFormPackMap(dealer);
  const defaultOnlineFormPacks = getDefaultOnlineFormPacks(dealer);
  return Object.keys(onlineFormPackMap).map(key => {
    const existingMatch = existingFormPackList.find(
      (fp: FormPack) => fp.id === `${dealer.id}-${key}`,
    );
    if (existingMatch) {
      return existingMatch;
    }
    const defaultMatch = defaultOnlineFormPacks.find(
      fp => fp.id === `${dealer.id}-${key}`,
    );
    if (defaultMatch) {
      return defaultMatch;
    }
    throw new Error('No existing or default match - should not happen');
  });
};

const OnlineFormPacks: FunctionComponent<Props> = ({
  match,
  actions,
  formPacks,
  dealer,
  history,
}) => {
  useEffect(() => {
    const { dealerId } = match.params;
    actions.formPacksRequest(dealerId);
  }, [match, actions]);

  const existingFormPackList = formPacks.formPacks.filter(
    (formPack: FormPack) => formPack.type === FormPackType.ONLINE,
  );
  const formPackList = getOnlineFormPacks(dealer, existingFormPackList);

  return (
    <SidebarContainer title={dealer.name} logo={dealer.logo}>
      <div className="form-packs-table">
        <DealerNav />
        <OnlineFormPackList
          dealer={dealer}
          list={formPackList}
          error={formPacks.error}
          history={history}
        />
      </div>
    </SidebarContainer>
  );
};

const mapStateToProps = ({ formPacks }: { formPacks: FormPacks }) => ({
  formPacks,
});

const mapDispatchToProps = (dispatch: Dispatch<string>) => ({
  actions: bindActionCreators(
    {
      ...formPacksActions,
    },
    dispatch,
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContentPage(OnlineFormPacks));
